import { Component, Input } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import {
  DraftIncomeSource,
  PayrollDetail,
  PayrollPaystubEntry,
  parseDraftDetail,
} from 'src/app/models/income-wizard-workflow';

@Component({
  selector: 'app-payroll-wizard-detail',
  templateUrl: './payroll-wizard-detail.component.html',
  styleUrls: ['./payroll-wizard-detail.component.less'],
  animations: [
    trigger('cardEnter', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(12px)' }),
        animate('280ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
    trigger('slideDown', [
      transition(':enter', [
        style({ opacity: 0, height: 0, overflow: 'hidden' }),
        animate('200ms ease-out', style({ opacity: 1, height: '*' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, height: '*', overflow: 'hidden' }),
        animate('160ms ease-in', style({ opacity: 0, height: 0 })),
      ]),
    ]),
  ],
})
export class PayrollWizardDetailComponent {
  @Input() applicantName = '';
  @Input() existingDraft: DraftIncomeSource | null = null;

  showMore = false;

  get detail(): PayrollDetail | null {
    return parseDraftDetail<PayrollDetail>(this.existingDraft);
  }

  get employerName(): string | null {
    return this.detail?.employerName ?? this.existingDraft?.employerName ?? null;
  }

  get employmentType(): string | null {
    return this.detail?.employmentType ?? null;
  }

  get statusBadgeClass(): string {
    const s = (this.employmentType ?? '').toLowerCase();
    if (s.includes('full')) return 'pr-badge-active';
    if (s.includes('part')) return 'pr-badge-part-time';
    if (s.includes('contract')) return 'pr-badge-contract';
    return 'pr-badge-unknown';
  }

  get streamPersonName(): string {
    return [this.detail?.firstName, this.detail?.lastName].filter(Boolean).join(' ').trim() || '';
  }

  get nameMatches(): boolean {
    if (!this.applicantName || !this.streamPersonName) return false;
    return this.applicantName.toLowerCase().trim() === this.streamPersonName.toLowerCase().trim();
  }

  get suggestedMonthly(): number | null {
    const amt = this.existingDraft?.suggestedAmount;
    return amt != null && amt > 0 ? amt : null;
  }

  get projectedAnnual(): number | null {
    const m = this.suggestedMonthly;
    return m != null && m > 0 ? Math.round(m * 12) : null;
  }

  get recentPaystubs(): PayrollPaystubEntry[] {
    return this.detail?.recentPaystubs ?? [];
  }

  get hasNoPaystubs(): boolean {
    return this.recentPaystubs.length === 0;
  }

  get incomeSourceLabel(): string {
    if (this.hasNoPaystubs && this.detail?.basePay != null && this.detail.basePay > 0)
      return 'No paystub records — income estimated from base pay rate';
    if (this.hasNoPaystubs)
      return 'No paystub records or pay rate available for this account';
    return '';
  }

  get hasMoreDetail(): boolean {
    return (
      this.detail?.basePay != null ||
      !!this.detail?.basePayUnit ||
      this.recentPaystubs.length > 0
    );
  }

  get failedSopChecks() {
    return this.detail?.sopChecks?.filter(c => !c.passed) ?? [];
  }

  hasFailedCheck(name: string): boolean {
    return this.failedSopChecks.some(c => c.checkName === name);
  }

  getCheckReason(name: string): string | null {
    return this.failedSopChecks.find(c => c.checkName === name)?.reason ?? null;
  }
}
